import {
    inject,
    ObjectGraph,
} from '@jet/environment/dependencies/object-graph';
import { ObjectGraphType } from './object-graph-type';

export interface LoggerFactory {
    build(label?: string): Logging;
}

export interface Logging {
    log(...messages: string[]): void;
    warn(...messages: string[]): void;
    error(...messages: string[]): void;
    flushMessages(): void;
    shouldQueueMessages: boolean;
}

type MessageType = 'log' | 'warn' | 'error';

interface QueuedMessage {
    type: MessageType;
    message: string;
}

// TODO: rdar://120555181 (Andorra Web: Should use sentry logger)
export class Logger implements Logging {
    readonly label?: string;
    private queuedMessages: QueuedMessage[] = [];
    shouldQueueMessages: boolean = false;

    constructor(label?: string) {
        this.label = label;
    }

    log(...messages: string[]): void {
        const message = this.formattedMessage(messages);
        if (this.shouldQueueMessages) {
            this.queueMessage(message, 'log');
            return;
        }
        console.log(message);
    }

    warn(...messages: string[]): void {
        const message = this.formattedMessage(messages);
        if (this.shouldQueueMessages) {
            this.queueMessage(message, 'warn');
            return;
        }
        console.warn(message);
    }

    error(...messages: string[]): void {
        const message = this.formattedMessage(messages);
        if (this.shouldQueueMessages) {
            this.queueMessage(message, 'error');
            return;
        }
        console.error(message);
    }

    flushMessages(): void {
        this.queuedMessages.forEach((queuedMessage) => {
            switch (queuedMessage.type) {
                case 'log':
                    console.log(queuedMessage.message);
                    break;
                case 'warn':
                    console.warn(queuedMessage.message);
                    break;
                case 'error':
                    console.error(queuedMessage.message);
                    break;
            }
        });
        this.queuedMessages = [];
    }

    // MARK: Static convenience methods

    static log(objectGraph: ObjectGraph, ...messages: string[]): void {
        const logger = inject(ObjectGraphType.logger, objectGraph);
        logger.log(...messages);
    }

    static warn(objectGraph: ObjectGraph, ...messages: string[]): void {
        const logger = inject(ObjectGraphType.logger, objectGraph);
        logger.warn(...messages);
    }

    static error(objectGraph: ObjectGraph, ...messages: string[]): void {
        const logger = inject(ObjectGraphType.logger, objectGraph);
        logger.error(...messages);
    }

    // MARK: Private

    private queueMessage(message: string, type: MessageType): void {
        this.queuedMessages.push({
            type,
            message,
        });
    }

    private get prefix(): string {
        if (this.label) {
            return `${this.label}: `;
        }

        return '';
    }

    private formattedMessage(messages: string[]): string {
        return `${this.prefix}${messages.join(' ')}`;
    }
}

// /**
//  * The Logger dependency provided by the host.
//  */

// export interface LoggerFactory {
//     /**
//      * Returns a Logger for the given component/class with the given name.
//      * It is expected that this is passed into the constructor or factory
//      * static method for a class/component and it uses `loggerFor` to
//      * construct its log.
//      * @example
//      * ```ts
//      *     class Widget {
//      *         private readonly log: Logger;
//      *
//      *         static configure(loggerFactory: LoggerFactory): Widget {
//      *             // Do your other setup here...
//      *             return new Widget(loggerFactory.loggerFor('Widget'), ...);
//      *         }
//      *
//      *         constructor(log: Logger, ...) {
//      *             this.log = logger;
//      *             // ...
//      *         }
//      *     }
//      * ```
//      * @param name The name of the class/component this logger is for
//      * @returns The logger instance
//      */
//     makeLogger(name: string): Logger;
// }

// export interface Logger {
//     /**
//      * @remarks
//      * Log a debug level message.
//      * Appropriate for verbose logging that explains steps/details of the inner state of
//      * a code unit.
//      *
//      * Example uses include in a size-constrain datastructure, logging when the size
//      * exceeds the threshold and elements are removed, or in a virtual scrolling
//      * component logging when a scroll event causes a new page of elements to be loaded.
//      *
//      * @param args - Arguments to log (same as console.debug)
//      * @returns Empty string (for use in brackets {} in svelte components)
//      */
//     debug(...args: any[]): void;

//     /**
//      * @remarks
//      * Log an info level message.
//      * Appropriate for informational messages that may be relevant to consumers of a code
//      * unit.
//      *
//      * Example uses include a router logging when transitions occur or a button logging
//      * clicks.
//      *
//      * @param args - Arguments to log (same as console.info)
//      * @return empty string (for use in brackets {} in svelte components)
//      */
//     info(...args: any[]): void;

//     /**
//      * @remarks
//      * Log a warn level message.
//      * Appropriate for situations where state has been (or likely will be) corrupted or
//      * invariants have been broken.
//      *
//      * Example uses include a data structure warning when it is used before being fully
//      * initialized.
//      *
//      * @param args - Arguments to log (same as console.warn)
//      * @return empty string (for use in brackets {} in svelte components)
//      */
//     warn(...args: any[]): void;

//     /**
//      * @remarks
//      * Log an error message.
//      * Appropriate for thrown errors or situations where the apps breaks or has to
//      * engage in fallback behavior to avoid a more catastrophic failure.
//      *
//      * @param args - Arguments to log (same as console.error)
//      * @return empty string (for use in brackets {} in svelte components)
//      */
//     error(...args: any[]): void;
// }
